import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Menu,
  FormControl,
  InputLabel,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import yaml from 'js-yaml';
import { exportToYAML, downloadYAMLFile, parseImportData, validateImportData } from '../../utils/activitySettingsImportExport';
import InitialNodeList from '../../components/Admin/InitialNodeList';
import { useInitialNodeGroups } from '../../hooks/useInitialNodeGroups';
import {
  createActivityTemplate,
  getUserTemplates,
  getTemplateById,
  updateActivityTemplate,
  deleteActivityTemplate
} from '../../services/activityTemplateService';
import DOMPurify from 'dompurify';
import { createGuidedActivity } from '../../services/templateService';
import {
  validateActivityName,
  prepareActivityCreationData,
  validateActivityCreationData
} from '../../utils/activityDataPreparation';

const chatModeOptions = [
  { value: 'sticker', label: 'Sticker 便利貼模式', description: '使用便利貼進行快速且生動的討論' },
  { value: 'chatroom', label: 'Chatroom 聊天室模式', description: '連續聊天室對話模式' },
  { value: 'tagged-forum', label: 'Tagged Forum 標籤論壇', description: '使用標籤分類的討論空間' },
  { value: 'workspace', label: 'Workspace 工作區', description: '結合節點選擇與工具面板的專業協作空間' }
];

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: '24px' }}>
      {value === index && children}
    </div>
  );
}

export default function TemplateBuilder({ onBack, userId }) {
  const [previewTab, setPreviewTab] = useState(0);
  const [partnerDialogTab, setPartnerDialogTab] = useState(0);
  const fileInputRef = useRef(null);
  
  // 範本管理相關狀態
  const [templates, setTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTab, setEditTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [cardMenu, setCardMenu] = useState({ anchor: null, template: null });
  const [isPublic, setIsPublic] = useState(false);
  const listRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [createActivityDialogOpen, setCreateActivityDialogOpen] = useState(false);
  const [newActivityName, setNewActivityName] = useState('');
  
  // 基本資訊
  const [activityInfo, setActivityInfo] = useState({
    title: '',
    content: ''
  });
  
  // 活動室設定
  const [settings, setSettings] = useState({
    chatmode: 'sticker'
  });
  
  // 使用 Hook 管理節點群組
  const {
    groups: nodeGroups,
    addGroup,
    updateGroup,
    removeGroup,
    addNodeToGroup,
    updateNodeInGroup,
    removeNodeFromGroup,
    moveNodeBetweenGroups,
    reorderGroups,
    reorderNodesInGroup,
    resetGroups
  } = useInitialNodeGroups([
    {
      id: 'group-default',
      name: '開始引導',
      timing: 'immediate',
      description: '這些節點會在活動開始時自動出現（此群組名稱不可修改）',
      nodes: [],
      isDefault: true
    }
  ]);
  
  // AI 夥伴
  const [partners, setPartners] = useState([]);
  const [partnerDialog, setPartnerDialog] = useState({ open: false, editing: null });
  const [currentPartner, setCurrentPartner] = useState({
    name: '',
    description: '',
    category: '',
    tags: '',
    roleTask: {
      primaryTask: '',
      coreMotivation: '',
      openingApproach: '',
      responseExamples: []
    },
    knowledgeBase: {
      keyTerminology: [],
      professionalSkills: '',
      backgroundKnowledge: ''
    },
    communicationStyle: {
      guardrails: [],
      personality: '',
      interactionMethod: '',
      communicationStyle: ''
    },
    responseTimeline: {
      responseMode: 'always',
      responseDelay: '',
      responseConditions: '',
      availabilitySchedule: ''
    },
    isPublicTemplate: false,
    groupId: null
  });
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // 載入用戶範本列表
  useEffect(() => {
    if (userId) {
      loadUserTemplates();
    }
  }, [userId]);

  const loadUserTemplates = async () => {
    try {
      setLoading(true);
      const data = await getUserTemplates(userId);
      setTemplates(data);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const makeSnapshot = (d) => JSON.stringify([
    d.name || '', d.description || '', d.activityInfo || {}, d.settings || {},
    d.initialNodeGroups || [], d.partners || [], Boolean(d.isPublic)
  ]);

  // 載入特定範本
  const loadTemplate = async (templateId) => {
    try {
      setLoading(true);
      const template = await getTemplateById(templateId, userId);
      
      setCurrentTemplateId(template.id);
      setTemplateName(template.name);
      setTemplateDescription(template.description);
      setIsPublic(Boolean(template.isPublic));
      setActivityInfo(template.activityInfo || { title: '', content: '' });
      setSettings(template.settings || { chatmode: 'sticker' });
      
      if (template.initialNodeGroups && Array.isArray(template.initialNodeGroups)) {
        resetGroups(template.initialNodeGroups);
      }
      
      setPartners(template.partners || []);
      setSavedSnapshot(makeSnapshot({
        name: template.name,
        description: template.description,
        activityInfo: template.activityInfo || { title: '', content: '' },
        settings: template.settings || { chatmode: 'sticker' },
        initialNodeGroups: Array.isArray(template.initialNodeGroups) ? template.initialNodeGroups : nodeGroups,
        partners: template.partners || [],
        isPublic: Boolean(template.isPublic)
      }));
      setSnackbar({ open: true, message: '教材已載入', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 儲存教材（更新現有教材）
  const handleSaveTemplate = async () => {
    if (!currentTemplateId) return;
    if (!templateName.trim()) {
      setSnackbar({ open: true, message: '請輸入教材名稱', severity: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const templateData = {
        userId,
        name: templateName,
        description: templateDescription,
        activityInfo,
        settings,
        initialNodeGroups: nodeGroups,
        partners,
        isPublic
      };
      await updateActivityTemplate(currentTemplateId, templateData);
      setSavedSnapshot(makeSnapshot(templateData));
      setSnackbar({ open: true, message: '教材已更新', severity: 'success' });
      await loadUserTemplates();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 刪除範本
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('確定要刪除此教材嗎？')) {
      return;
    }

    try {
      setLoading(true);
      await deleteActivityTemplate(templateId, userId);
      
      if (currentTemplateId === templateId) {
        // 如果刪除的是當前範本，清空編輯器
        clearEditor();
      }
      
      await loadUserTemplates();
      setSnackbar({ open: true, message: '教材已刪除', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 新增範本：直接建立一個教材物件
  const handleNewTemplate = async () => {
    const defaultGroups = [{
      id: 'group-default',
      name: '開始引導',
      timing: 'immediate',
      description: '這些節點會在活動開始時自動出現（此群組名稱不可修改）',
      nodes: [],
      isDefault: true
    }];
    const data = {
      userId,
      name: '未命名單元',
      description: '',
      activityInfo: { title: '', content: '' },
      settings: { chatmode: 'sticker' },
      initialNodeGroups: defaultGroups,
      partners: [],
      isPublic: false
    };
    try {
      setLoading(true);
      const result = await createActivityTemplate(data);
      setCurrentTemplateId(result.template.id);
      setTemplateName(data.name);
      setTemplateDescription('');
      setIsPublic(false);
      setActivityInfo(data.activityInfo);
      setSettings(data.settings);
      resetGroups(defaultGroups);
      setPartners([]);
      setSavedSnapshot(makeSnapshot(data));
      await loadUserTemplates();
      setSnackbar({ open: true, message: '已建立新單元，編輯後請點選「儲存教材」', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 清空編輯器
  const clearEditor = () => {
    setCurrentTemplateId(null);
    setSavedSnapshot(null);
    setTemplateName('');
    setTemplateDescription('');
    setIsPublic(false);
    setActivityInfo({ title: '', content: '' });
    setSettings({ chatmode: 'sticker' });
    resetGroups([{
      id: 'group-default',
      name: '開始引導',
      timing: 'immediate',
      description: '這些節點會在活動開始時自動出現（此群組名稱不可修改）',
      nodes: [],
      isDefault: true
    }]);
    setPartners([]);
  };

  // 夥伴管理
  const handleOpenPartnerDialog = (partner = null, index = null) => {
    if (partner) {
      setCurrentPartner({ ...partner });
      setPartnerDialog({ open: true, editing: index });
    } else {
      setCurrentPartner({
        name: '',
        description: '',
        category: '',
        tags: '',
        roleTask: {
          primaryTask: '',
          coreMotivation: '',
          openingApproach: '',
          responseExamples: []
        },
        knowledgeBase: {
          keyTerminology: [],
          professionalSkills: '',
          backgroundKnowledge: ''
        },
        communicationStyle: {
          guardrails: [],
          personality: '',
          interactionMethod: '',
          communicationStyle: ''
        },
        responseTimeline: {
          responseMode: 'always',
          responseDelay: '',
          responseConditions: '',
          availabilitySchedule: ''
        },
        isPublicTemplate: false,
        groupId: null
      });
      setPartnerDialog({ open: true, editing: null });
    }
  };

  const handleClosePartnerDialog = () => {
    setPartnerDialog({ open: false, editing: null });
    setCurrentPartner({
      name: '',
      description: '',
      category: '',
      tags: '',
      roleTask: {
        primaryTask: '',
        coreMotivation: '',
        openingApproach: '',
        responseExamples: []
      },
      knowledgeBase: {
        keyTerminology: [],
        professionalSkills: '',
        backgroundKnowledge: ''
      },
      communicationStyle: {
        guardrails: [],
        personality: '',
        interactionMethod: '',
        communicationStyle: ''
      },
      responseTimeline: {
        responseMode: 'always',
        responseDelay: '',
        responseConditions: '',
        availabilitySchedule: ''
      },
      isPublicTemplate: false,
      groupId: null
    });
  };

  const handleSavePartner = () => {
    if (!currentPartner.name) {
      setSnackbar({ open: true, message: '請填寫夥伴名稱', severity: 'warning' });
      return;
    }

    if (partnerDialog.editing !== null) {
      // 編輯現有夥伴
      const updated = [...partners];
      updated[partnerDialog.editing] = { ...currentPartner };
      setPartners(updated);
      setSnackbar({ open: true, message: '夥伴已更新', severity: 'success' });
    } else {
      // 新增夥伴
      setPartners([...partners, { ...currentPartner }]);
      setSnackbar({ open: true, message: '夥伴已新增', severity: 'success' });
    }
    
    handleClosePartnerDialog();
  };

  const handleDeletePartner = (index) => {
    if (window.confirm('確定要刪除此夥伴嗎？')) {
      const updated = partners.filter((_, i) => i !== index);
      setPartners(updated);
      setSnackbar({ open: true, message: '夥伴已刪除', severity: 'info' });
    }
  };

  // 創建活動
  const handleCreateActivity = async () => {
    // 使用工具函數驗證活動名稱
    const nameValidation = validateActivityName(newActivityName);
    if (!nameValidation.isValid) {
      setSnackbar({ open: true, message: nameValidation.error, severity: 'warning' });
      return;
    }

    try {
      setLoading(true);
      
      // 使用工具函數準備活動數據
      const activityData = prepareActivityCreationData({
        userId,
        activityName: newActivityName,
        activityInfo,
        settings,
        nodeGroups,
        partners
      });

      // 驗證準備好的數據
      const validation = validateActivityCreationData(activityData);
      if (!validation.isValid) {
        setSnackbar({ 
          open: true, 
          message: validation.errors.join(', '), 
          severity: 'warning' 
        });
        return;
      }

      const result = await createGuidedActivity(activityData);
      
      setSnackbar({ 
        open: true, 
        message: `活動「${newActivityName}」已成功創建！`, 
        severity: 'success' 
      });
      
      setCreateActivityDialogOpen(false);
      setNewActivityName('');
      
      // 如果有 onActivityCreated 回調，調用它
      // 這樣可以跳轉到新創建的活動
      if (result.activity) {
        console.log('Activity created:', result.activity);
      }
    } catch (error) {
      console.error('Error creating activity:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || '創建活動失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 匯出功能
  const handleExport = () => {
    try {
      const exportData = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        activityInfo,
        settings: {
          ...settings,
          initialNodeGroups: nodeGroups
        },
        partners: partners.map(p => ({
          ...p,
          isExternal: false // 自建模板都是完整配置
        }))
      };

      const yamlContent = exportToYAML(exportData);
      const fileName = `template-${activityInfo.title || 'untitled'}-${Date.now()}.yaml`;
      downloadYAMLFile(yamlContent, fileName);

      setSnackbar({ open: true, message: '教材已匯出', severity: 'success' });
    } catch (error) {
      console.error('匯出失敗:', error);
      setSnackbar({ open: true, message: '匯出失敗', severity: 'error' });
    }
  };

  // 匯入功能
  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const importData = parseImportData(content, file.name);
        validateImportData(importData);

        // 載入資料
        if (importData.activityInfo) {
          setActivityInfo(importData.activityInfo);
        }
        
        if (importData.settings) {
          const { initialNodeGroups, ...otherSettings } = importData.settings;
          setSettings(otherSettings);
          
          if (initialNodeGroups && Array.isArray(initialNodeGroups)) {
            resetGroups(initialNodeGroups);
          }
        }
        
        if (importData.partners && Array.isArray(importData.partners)) {
          setPartners(importData.partners);
        }

        setSnackbar({ open: true, message: '教材已成功匯入', severity: 'success' });
      } catch (error) {
        console.error('匯入失敗:', error);
        setSnackbar({ open: true, message: `匯入失敗：${error.message}`, severity: 'error' });
      }
    };

    reader.onerror = () => {
      setSnackbar({ open: true, message: '檔案讀取失敗', severity: 'error' });
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const nodePlan = {
    title: "新增節點",
    label: "節點標題",
    tags: "initial",
    helperMsg: "請為節點提供標題和內容",
    saffoldBody: ["標題：", "內容："],
    scaffoldWords: ["標題：", "內容："],
    newEdge: true,
    note: null,
    intro: "請在這邊填寫節點內容",
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        // 基本資訊
        return (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              活動基本資訊
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              設定活動的標題和描述內容
            </Typography>
            
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={10}
              label="活動標題"
              value={activityInfo.title}
              onChange={(e) => setActivityInfo({ ...activityInfo, title: e.target.value })}
              placeholder="例如：協作式問題解決工作坊"
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={20}
              label="活動內容說明"
              value={activityInfo.content}
              onChange={(e) => setActivityInfo({ ...activityInfo, content: e.target.value })}
              placeholder="描述活動的目標、流程和預期成果..."
              helperText="此內容會顯示在引導單元中"
            />
          </Box>
        );

      case 1:
        // 活動室設定
        return (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              活動室模式設定
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              選擇適合的討論模式
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>活動室模式</InputLabel>
              <Select
                value={settings.chatmode}
                label="活動室模式"
                onChange={(e) => setSettings({ ...settings, chatmode: e.target.value })}
              >
                {chatModeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography variant="body1">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                當前選擇：
              </Typography>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                {chatModeOptions.find(o => o.value === settings.chatmode)?.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {chatModeOptions.find(o => o.value === settings.chatmode)?.description}
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        // 引導單元
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              設定引導單元群組
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              創建和管理活動開始時的引導單元
            </Typography>
            
            <InitialNodeList
              groups={nodeGroups}
              onAddGroup={addGroup}
              onUpdateGroup={updateGroup}
              onRemoveGroup={removeGroup}
              onAddNode={addNodeToGroup}
              onUpdateNode={updateNodeInGroup}
              onRemoveNode={removeNodeFromGroup}
              onReorderGroups={reorderGroups}
              onReorderNodes={reorderNodesInGroup}
              onMoveNodeBetweenGroups={moveNodeBetweenGroups}
              nodePlan={nodePlan}
              activityContent={activityInfo.content}
            />
          </Box>
        );

      case 3:
        // AI 夥伴
        return (
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  AI 夥伴配置
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  添加和配置 AI 協作夥伴
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenPartnerDialog()}
              >
                新增夥伴
              </Button>
            </Box>

            {partners.length === 0 ? (
              <Alert severity="info">
                目前沒有 AI 夥伴。點擊「新增夥伴」按鈕來添加第一個夥伴。
              </Alert>
            ) : (
              <Stack spacing={2}>
                {partners.map((partner, index) => (
                  <Card key={index}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {partner.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {partner.description || '無描述'}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {partner.category && (
                              <Chip label={partner.category} size="small" color="primary" variant="outlined" />
                            )}
                            {partner.tags && (
                              <Chip label={partner.tags.split(',')[0]} size="small" variant="outlined" />
                            )}
                            <Chip label={partner.responseTimeline?.responseMode || 'always'} size="small" variant="outlined" />
                          </Stack>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton onClick={() => handleOpenPartnerDialog(partner, index)} color="primary">
                            <PreviewIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeletePartner(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        );

      case 4:
        // 預覽與匯出
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              預覽與匯出
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              檢視完整配置並匯出 YAML 檔案
            </Typography>

            <Tabs value={previewTab} onChange={(e, v) => setPreviewTab(v)} sx={{ mb: 2 }}>
              <Tab label="摘要" />
              <Tab label="YAML 預覽" />
            </Tabs>

            <TabPanel value={previewTab} index={0}>
              <Stack spacing={3}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    基本資訊
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">標題：</Typography>
                  <Typography variant="body1" paragraph>
                    {activityInfo.title || '(未設定)'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">內容：</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {activityInfo.content || '(未設定)'}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    活動室設定
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Chip 
                    label={chatModeOptions.find(o => o.value === settings.chatmode)?.label} 
                    color="primary"
                  />
                </Paper>

                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    引導單元群組
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    共 {nodeGroups.length} 個群組，
                    {nodeGroups.reduce((sum, g) => sum + g.nodes.length, 0)} 個節點
                  </Typography>
                </Paper>

                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    AI 夥伴
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    共 {partners.length} 個夥伴
                  </Typography>
                  {partners.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                      {partners.map((p, i) => (
                        <Chip key={i} label={p.name} size="small" />
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Stack>
            </TabPanel>

            <TabPanel value={previewTab} index={1}>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', maxHeight: '500px', overflow: 'auto' }}>
                <pre style={{ 
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}>
                  {(() => {
                    try {
                      const exportData = {
                        version: '1.0',
                        exportDate: new Date().toISOString(),
                        activityInfo,
                        settings: {
                          ...settings,
                          initialNodeGroups: nodeGroups
                        },
                        partners: partners.map(p => ({
                          ...p,
                          isExternal: false
                        }))
                      };
                      return yaml.dump(exportData, {
                        indent: 2,
                        lineWidth: -1,
                        noRefs: true,
                        sortKeys: false
                      });
                    } catch (error) {
                      return '無法生成 YAML 預覽';
                    }
                  })()}
                </pre>
              </Paper>
            </TabPanel>
          </Box>
        );

      default:
        return null;
    }
  };

  const filteredTemplates = templates.filter((t) =>
    [t.activityInfo?.title, t.name]
      .some((name) => (name || '').toLowerCase().includes(templateSearch.trim().toLowerCase()))
  );
  const countGroups = (t) => (Array.isArray(t.initialNodeGroups) ? t.initialNodeGroups.length : null);
  const countNodes = (t) =>
    Array.isArray(t.initialNodeGroups)
      ? t.initialNodeGroups.reduce((sum, g) => sum + (g.nodes?.length || 0), 0)
      : null;
  const isDirty = Boolean(currentTemplateId) && savedSnapshot !== null && savedSnapshot !== makeSnapshot({
    name: templateName, description: templateDescription, activityInfo, settings,
    initialNodeGroups: nodeGroups, partners, isPublic
  });
  const currentTemplate = templates.find((t) => t.id === currentTemplateId);
  const totalNodes = nodeGroups.reduce((sum, g) => sum + (g.nodes?.length || 0), 0);
  const coverGradients = [
    'linear-gradient(135deg,#1a237e,#7e57c2)',
    'linear-gradient(135deg,#2e7d32,#a5d6a7)',
    'linear-gradient(135deg,#0277bd,#4dd0e1)',
    'linear-gradient(135deg,#ef6c00,#ffcc80)'
  ];
  const coverFor = (id) => {
    const str = String(id || '0');
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
    return coverGradients[h % coverGradients.length];
  };
  const editTabSteps = [0, 1, 3, 4];
  const editTabLabels = ['基本資訊', '活動室設定', 'AI 夥伴', '預覽與匯出'];
  const startPresentation = () => setPresentationOpen(true);
  const sanitizeHtml = (html) => DOMPurify.sanitize(String(html || ''));
  const startCreateActivity = () => {
    setNewActivityName(activityInfo.title || templateName || '');
    setCreateActivityDialogOpen(true);
  };

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pb: 4 }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg,#1a1a4e 0%,#4a3aa7 60%,#7c6ae0 100%)',
          color: 'white',
          py: 3,
          px: 3
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={onBack} sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="bold">單元管理</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                層級化 CRUD 管理，依單元 → 活動 → 任務 建立教材架構
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => listRef.current?.scrollIntoView({ behavior: 'smooth' })}
              variant="contained"
              sx={{ bgcolor: '#6c5ce7', borderRadius: 5, '&:hover': { bgcolor: '#5b4bd5' } }}
            >
              教材清單 →
            </Button>
            <Button
              startIcon={<SaveIcon />}
              onClick={handleSaveTemplate}
              disabled={loading || !currentTemplateId}
              variant="contained"
              sx={{ bgcolor: '#6c5ce7', borderRadius: 5, '&:hover': { bgcolor: '#5b4bd5' } }}
            >
              儲存教材
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 1400,
          mx: 'auto',
          mt: 3,
          px: 2,
          display: 'flex',
          gap: 3,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start'
        }}
      >
        {/* 左側：單元清單 */}
        <Paper ref={listRef} sx={{ p: 2, width: { xs: '100%', md: 360 }, flexShrink: 0, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>單元清單</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="搜尋單元名稱..."
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button startIcon={<AddIcon />} onClick={handleNewTemplate} variant="contained" sx={{ bgcolor: '#6c5ce7' }}>
              新增單元
            </Button>
            <Button startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()}>
              匯入
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".yaml,.yml,.json"
              style={{ display: 'none' }}
              onChange={handleImport}
            />
          </Box>
          {loading && templates.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredTemplates.length === 0 ? (
            <Alert severity="info">
              {templates.length === 0 ? '還沒有任何單元。點擊「新增單元」開始創建！' : '找不到符合的單元'}
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {filteredTemplates.map((template) => {
                const selected = currentTemplateId === template.id;
                const gc = countGroups(template);
                const nc = countNodes(template);
                return (
                  <Box
                    key={template.id}
                    onClick={() => loadTemplate(template.id)}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      p: 1.5,
                      border: 1,
                      borderColor: selected ? '#6c5ce7' : 'divider',
                      bgcolor: selected ? '#f1eefe' : 'white',
                      borderRadius: 2,
                      cursor: 'pointer'
                    }}
                  >
                    <Box sx={{ width: 64, height: 64, borderRadius: 2, flexShrink: 0, background: coverFor(template.id) }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight="bold" noWrap>
                          {template.activityInfo?.title || template.name}
                        </Typography>
                        <Chip size="small" label={template.isPublic ? '已發佈' : '草稿'} color={template.isPublic ? 'success' : 'default'} variant={template.isPublic ? 'filled' : 'outlined'} />
                      </Box>
                      {template.description && (
                        <Typography variant="body2" color="text.secondary" noWrap>{template.description}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" component="div">
                        {gc !== null ? `${gc} 活動 · ${nc} 任務 · ` : ''}
                        {new Date(template.updatedAt).toLocaleDateString('zh-TW')}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      aria-label="更多"
                      onClick={(e) => { e.stopPropagation(); setCardMenu({ anchor: e.currentTarget, template }); }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
          )}
          <Menu
            anchorEl={cardMenu.anchor}
            open={Boolean(cardMenu.anchor)}
            onClose={() => setCardMenu({ anchor: null, template: null })}
          >
            <MenuItem onClick={() => { const t = cardMenu.template; setCardMenu({ anchor: null, template: null }); loadTemplate(t.id); }}>
              開啟
            </MenuItem>
            <MenuItem sx={{ color: 'error.main' }} onClick={() => { const t = cardMenu.template; setCardMenu({ anchor: null, template: null }); handleDeleteTemplate(t.id); }}>
              刪除教材
            </MenuItem>
          </Menu>
        </Paper>

        {/* 右側：編輯區 */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {isDirty && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              教材內容已變更，請點選右上角「儲存教材」以保存更新。
            </Alert>
          )}
          <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: 2,
                  flexShrink: 0,
                  background: coverFor(currentTemplateId || templateName)
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h5" fontWeight="bold" noWrap>
                    {activityInfo.title || templateName || '未命名教材'}
                  </Typography>
                  <Chip label={isPublic ? '已發佈' : '草稿'} color={isPublic ? 'success' : 'default'} size="small" />
                </Box>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {activityInfo.content ? activityInfo.content.slice(0, 120) : templateDescription || '尚無說明'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  1 單元 · {nodeGroups.length} 活動 · {totalNodes} 任務
                  {currentTemplate?.updatedAt && ` · 最後修改：${new Date(currentTemplate.updatedAt).toLocaleString('zh-TW')}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setEditDialogOpen(true)}>
                  編輯
                </Button>
                <IconButton aria-label="更多" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                  <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                  <MenuItem onClick={() => { setMenuAnchor(null); handleExport(); }}>匯出教材</MenuItem>
                  <MenuItem onClick={() => { setMenuAnchor(null); setEditTab(3); setEditDialogOpen(true); }}>預覽與匯出 YAML</MenuItem>
                  {currentTemplateId && (
                    <MenuItem sx={{ color: 'error.main' }} onClick={() => { setMenuAnchor(null); handleDeleteTemplate(currentTemplateId); }}>
                      刪除教材
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>教材內容架構</Typography>
            <InitialNodeList
              compact
              groups={nodeGroups}
              onAddGroup={addGroup}
              onUpdateGroup={updateGroup}
              onRemoveGroup={removeGroup}
              onAddNode={addNodeToGroup}
              onUpdateNode={updateNodeInGroup}
              onRemoveNode={removeNodeFromGroup}
              onReorderGroups={reorderGroups}
              onReorderNodes={reorderNodesInGroup}
              onMoveNodeBetweenGroups={moveNodeBetweenGroups}
              nodePlan={nodePlan}
              activityContent={activityInfo.content}
            />
          </Paper>

          <Paper
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: '#f1eefe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Box>
              <Typography fontWeight="bold" color="#4a3aa7">展示模式（群體授課）</Typography>
              <Typography variant="body2" color="text.secondary">可直接調用單元內容進行同步教學呈現</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={startPresentation}
                sx={{ bgcolor: '#6c5ce7', borderRadius: 2 }}
              >
                開始展示
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={startCreateActivity}
                sx={{ borderRadius: 2 }}
              >
                建立活動
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* 教材資訊編輯對話框 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>編輯教材資訊</DialogTitle>
        <DialogContent>
          <Tabs value={editTab} onChange={(e, v) => setEditTab(v)} variant="scrollable">
            {editTabLabels.map((l) => <Tab key={l} label={l} />)}
          </Tabs>
          <Box sx={{ pt: 2 }}>{renderStepContent(editTabSteps[editTab])}</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* 夥伴編輯對話框 */}
      <Dialog
        open={partnerDialog.open}
        onClose={handleClosePartnerDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {partnerDialog.editing !== null ? '編輯 AI 夥伴' : '新增 AI 夥伴'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={partnerDialogTab} onChange={(e, v) => setPartnerDialogTab(v)} sx={{ mb: 2 }}>
            <Tab label="基本資訊" />
            <Tab label="角色任務" />
            <Tab label="知識庫" />
            <Tab label="溝通風格" />
            <Tab label="回應時機" />
          </Tabs>

          <TabPanel value={partnerDialogTab} index={0}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="夥伴名稱"
                value={currentPartner.name}
                onChange={(e) => setCurrentPartner({ ...currentPartner, name: e.target.value })}
                required
              />
              
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={10}
                label="描述"
                value={currentPartner.description}
                onChange={(e) => setCurrentPartner({ ...currentPartner, description: e.target.value })}
                helperText="簡短描述這個 AI 夥伴的特色和功能"
              />
              
              <TextField
                fullWidth
                label="分類"
                value={currentPartner.category}
                onChange={(e) => setCurrentPartner({ ...currentPartner, category: e.target.value })}
                placeholder="例如：education, art, science"
              />

              <TextField
                fullWidth
                label="標籤"
                value={currentPartner.tags}
                onChange={(e) => setCurrentPartner({ ...currentPartner, tags: e.target.value })}
                placeholder="以逗號分隔，例如：教育,蘇格拉底,提問,批判性思考"
                helperText="用於搜尋和分類的關鍵字"
              />
            </Stack>
          </TabPanel>

          <TabPanel value={partnerDialogTab} index={1}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={10}
                label="主要任務 (Primary Task)"
                value={currentPartner.roleTask.primaryTask}
                onChange={(e) => setCurrentPartner({
                  ...currentPartner,
                  roleTask: { ...currentPartner.roleTask, primaryTask: e.target.value }
                })}
                helperText="描述這個 AI 夥伴的核心任務"
              />

              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={10}
                label="核心動機 (Core Motivation)"
                value={currentPartner.roleTask.coreMotivation}
                onChange={(e) => setCurrentPartner({
                  ...currentPartner,
                  roleTask: { ...currentPartner.roleTask, coreMotivation: e.target.value }
                })}
                helperText="解釋 AI 夥伴的核心價值觀和動機"
              />

              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={8}
                label="開場方式 (Opening Approach)"
                value={currentPartner.roleTask.openingApproach}
                onChange={(e) => setCurrentPartner({
                  ...currentPartner,
                  roleTask: { ...currentPartner.roleTask, openingApproach: e.target.value }
                })}
                helperText="第一次與學生互動時的開場白"
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  回應範例 (Response Examples)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={20}
                  value={currentPartner.roleTask.responseExamples.join('\n')}
                  onChange={(e) => setCurrentPartner({
                    ...currentPartner,
                    roleTask: {
                      ...currentPartner.roleTask,
                      responseExamples: e.target.value.split('\n')
                    }
                  })}
                  placeholder="每行一個範例\n例如：\n這是個很好的觀點！那麼，你認為為什麼會這樣呢？\n有趣的想法。如果我們從另一個角度來看，你覺得會有什麼不同？"
                  helperText="每行輸入一個典型的回應範例"
                />
              </Box>
            </Stack>
          </TabPanel>

          <TabPanel value={partnerDialogTab} index={2}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  核心術語 (Key Terminology)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={20}
                  value={currentPartner.knowledgeBase.keyTerminology.join('\n')}
                  onChange={(e) => setCurrentPartner({
                    ...currentPartner,
                    knowledgeBase: {
                      ...currentPartner.knowledgeBase,
                      keyTerminology: e.target.value.split('\n')
                    }
                  })}
                  placeholder="每行一個專業術語\n例如：\n蘇格拉底式提問 (Socratic Questioning)\n批判性思維 (Critical Thinking)"
                  helperText="列出 AI 夥伴應該熟悉的專業術語"
                />
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={15}
                label="專業技能 (Professional Skills)"
                value={currentPartner.knowledgeBase.professionalSkills}
                onChange={(e) => setCurrentPartner({
                  ...currentPartner,
                  knowledgeBase: { ...currentPartner.knowledgeBase, professionalSkills: e.target.value }
                })}
                helperText="描述 AI 夥伴擁有的專業技能"
              />

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={15}
                label="背景知識 (Background Knowledge)"
                value={currentPartner.knowledgeBase.backgroundKnowledge}
                onChange={(e) => setCurrentPartner({
                  ...currentPartner,
                  knowledgeBase: { ...currentPartner.knowledgeBase, backgroundKnowledge: e.target.value }
                })}
                helperText="描述 AI 夥伴的背景和專業領域"
              />
            </Stack>
          </TabPanel>

          <TabPanel value={partnerDialogTab} index={3}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  行為準則 (Guardrails)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={20}
                  value={currentPartner.communicationStyle.guardrails.join('\n')}
                  onChange={(e) => setCurrentPartner({
                    ...currentPartner,
                    communicationStyle: {
                      ...currentPartner.communicationStyle,
                      guardrails: e.target.value.split('\n')
                    }
                  })}
                  placeholder="每行一個行為準則\n例如：\n不直接提供答案，而是引導學生自己思考\n避免過於艱深的問題造成學生挫折"
                  helperText="定義 AI 夥伴應該遵守的行為準則"
                />
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                label="個性特質 (Personality)"
                value={currentPartner.communicationStyle.personality}
                onChange={(e) => setCurrentPartner({
                  ...currentPartner,
                  communicationStyle: { ...currentPartner.communicationStyle, personality: e.target.value }
                })}
                placeholder="例如：溫和但堅持、充滿好奇、善於傾聽"
                helperText="描述 AI 夥伴的個性特質"
              />

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={15}
                label="互動方式 (Interaction Method)"
                value={currentPartner.communicationStyle.interactionMethod}
                onChange={(e) => setCurrentPartner({
                  ...currentPartner,
                  communicationStyle: { ...currentPartner.communicationStyle, interactionMethod: e.target.value }
                })}
                helperText="描述 AI 夥伴與學生互動的具體方式"
              />

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={15}
                label="溝通風格 (Communication Style)"
                value={currentPartner.communicationStyle.communicationStyle}
                onChange={(e) => setCurrentPartner({
                  ...currentPartner,
                  communicationStyle: { ...currentPartner.communicationStyle, communicationStyle: e.target.value }
                })}
                helperText="描述 AI 夥伴的整體溝通風格"
              />
            </Stack>
          </TabPanel>

          <TabPanel value={partnerDialogTab} index={4}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>回應模式</InputLabel>
                <Select
                  value={currentPartner.responseTimeline.responseMode}
                  label="回應模式"
                  onChange={(e) => setCurrentPartner({
                    ...currentPartner,
                    responseTimeline: { 
                      responseMode: e.target.value,
                      responseDelay: '',
                      responseConditions: '',
                      availabilitySchedule: ''
                    }
                  })}
                >
                  <MenuItem value="always">總是回應</MenuItem>
                  <MenuItem value="conditional">條件回應</MenuItem>
                </Select>
              </FormControl>

              {currentPartner.responseTimeline.responseMode === 'conditional' && (
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={20}
                  label="回應條件"
                  value={currentPartner.responseTimeline.responseConditions}
                  onChange={(e) => setCurrentPartner({
                    ...currentPartner,
                    responseTimeline: { ...currentPartner.responseTimeline, responseConditions: e.target.value }
                  })}
                  placeholder="描述在什麼情況下 AI 夥伴會介入回應。例如：&#10;- 當學生提出想法、需要深化思考時&#10;- 當討論進入關鍵點時&#10;- 給予學生充分的思考時間後"
                  helperText="描述 AI 夥伴介入回應的具體條件"
                  required
                />
              )}

              {currentPartner.responseTimeline.responseMode === 'always' && (
                <Alert severity="info">
                  AI 夥伴將在所有情況下都會參與回應和互動
                </Alert>
              )}
            </Stack>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePartnerDialog}>取消</Button>
          {partnerDialogTab < 4 ? (
            <Button 
              onClick={() => setPartnerDialogTab(partnerDialogTab + 1)} 
              variant="contained"
            >
              下一步
            </Button>
          ) : (
            <Button onClick={handleSavePartner} variant="contained">
              {partnerDialog.editing !== null ? '更新' : '新增'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 展示對話框：展示單元下所有活動與任務 */}
      <Dialog open={presentationOpen} onClose={() => setPresentationOpen(false)} fullScreen>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {activityInfo.title || templateName || '未命名單元'}
          <Button onClick={() => setPresentationOpen(false)}>關閉展示</Button>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxWidth: 960, mx: 'auto' }}>
            {activityInfo.content && (
              <Box sx={{ mb: 3, '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(activityInfo.content) }} />
            )}
            {nodeGroups.map((group, gi) => (
              <Paper key={group.id || gi} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h5" fontWeight="bold">活動 {gi + 1}：{group.name}</Typography>
                {group.description && (
                  <Typography color="text.secondary" sx={{ mb: 2 }}>{group.description}</Typography>
                )}
                {(group.nodes || []).length === 0 ? (
                  <Typography color="text.secondary">此活動尚無任務</Typography>
                ) : (
                  (group.nodes || []).map((node, ni) => (
                    <Box key={node.id || ni} sx={{ mt: 2, p: 2, bgcolor: '#f5f7fa', borderRadius: 2 }}>
                      <Typography variant="h6" fontWeight="bold">任務 {ni + 1}：{node.title || '未命名任務'}</Typography>
                      <Box sx={{ mt: 1, '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(node.content) }} />
                    </Box>
                  ))
                )}
              </Paper>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* 創建活動對話框 */}
      <Dialog
        open={createActivityDialogOpen}
        onClose={() => setCreateActivityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          使用教材創建活動
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Alert severity="info">
              將使用目前教材的設定創建一個新的活動
            </Alert>
            <TextField
              fullWidth
              label="活動名稱"
              value={newActivityName}
              onChange={(e) => setNewActivityName(e.target.value)}
              required
              placeholder="例如：第一週討論活動"
              autoFocus
            />
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                教材配置摘要：
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 活動室模式：{chatModeOptions.find(o => o.value === settings.chatmode)?.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 引導單元：{nodeGroups.reduce((sum, g) => sum + g.nodes.length, 0)} 個
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • AI 夥伴：{partners.length} 個
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateActivityDialogOpen(false)}>取消</Button>
          <Button 
            onClick={handleCreateActivity} 
            variant="contained"
            disabled={loading || !newActivityName.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            創建活動
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
